import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Input } from 'components/FormFields';
import FormModal from 'components/FormModal';
import { useAdminAccountVenues } from 'hooks/useAccountVenues';
import useAdminRouteContext from 'hooks/useAdminRouteContext';
import { useCreateForm } from '../gql';
import { brand } from '../shared/brand';
import { formBuilderPath } from '../shared/routes';
import FormsSelect from './FormsSelect';

// "New form": a name and the account it belongs to. Enter submits (the
// modal is a <form>), Esc closes. On success it opens the builder and hands
// it a toast through router state. Server field errors land under the
// input they're about; anything else shows at the bottom.

const NAME_ID = 'new-form-name';
const ACCOUNT_ID = 'new-form-account';

const NewFormModal = ({ open, onClose, defaultAccountId }) => {
  const history = useHistory();
  const { routePrefix } = useAdminRouteContext();
  const { venues, loading } = useAdminAccountVenues();
  const createForm = useCreateForm();
  const [name, setName] = useState('');
  const [account, setAccount] = useState('');
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setName('');
    setAccount(defaultAccountId ? String(defaultAccountId) : '');
    setErrors({});
    setGlobalError(null);
    // FormModal portals on its next render; focus the name once it's there.
    const timer = setTimeout(() => {
      const input = document.getElementById(NAME_ID);
      if (input) input.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open, defaultAccountId]);

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = async () => {
    if (submitting) return;
    const trimmed = name.trim();
    const local = {};
    if (!trimmed) local.name = 'Give the form a name';
    if (!account) local.account_id = 'Choose the account it belongs to';
    setGlobalError(null);
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    setErrors({});
    setSubmitting(true);
    const { data, error } = await createForm({
      name: trimmed,
      account_id: Number(account),
    });
    setSubmitting(false);
    if (error) {
      setErrors(error.fields);
      const shown = error.fields.name || error.fields.account_id;
      setGlobalError(shown ? null : error.global);
      return;
    }
    const venue = venues.find((v) => String(v.id) === account);
    const accountName =
      (data.account && data.account.name) ||
      (venue && venue.name) ||
      'the account';
    onClose();
    history.push(formBuilderPath(routePrefix, data.id), {
      toast: `“${data.name}” created under ${accountName}`,
    });
  };

  const options = venues.map((v) => ({ label: v.name, value: String(v.id) }));

  return (
    <FormModal
      isOpen={open}
      onClose={close}
      title="New form"
      subtitle="Name it and pick the account it belongs to"
      submitText="Create form"
      onSubmit={submit}
      isSubmitting={submitting}
      maxWidth="460px"
    >
      <div style={{ display: 'grid', gap: 16 }}>
        <Input
          id={NAME_ID}
          label="Form name"
          aria-label="Form name"
          required
          value={name}
          maxLength={200}
          placeholder="e.g. 2027 Race Registration"
          error={!!errors.name}
          helperText={errors.name}
          aria-invalid={errors.name ? 'true' : undefined}
          onChange={(e) => setName(e.target.value)}
        />
        <FormsSelect
          id={ACCOUNT_ID}
          label="Account"
          required
          value={account}
          placeholder={
            loading && !options.length
              ? 'Loading accounts…'
              : 'Choose an account'
          }
          options={options}
          error={errors.account_id || null}
          onChange={(id) => setAccount(id)}
        />
        <p
          style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, opacity: 0.7 }}
        >
          The track or series this form is for. People see it under that account
          in the {brand().name} app, and you can change it later in Form
          Settings.
        </p>
        {globalError ? (
          <p
            role="alert"
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 600,
              color: '#ED4337',
            }}
          >
            {globalError}
          </p>
        ) : null}
      </div>
    </FormModal>
  );
};

NewFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  defaultAccountId: PropTypes.number,
};

NewFormModal.defaultProps = {
  defaultAccountId: null,
};

export default NewFormModal;
